import React from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FOR_BANK_SECTIONS, FOR_BANK_SUB_SECTIONS } from 'constants/businessToolSections/forBank';

import useCheckSubSectionAccess from 'hooks/useCheckSubSectionAccess';
import { useDebouncedSave } from 'hooks/useDebounceSave';

import { FormSectionCard } from 'components/_basic/FormSectionCard';
import { FormSectionFilledCard } from 'components/_basic/FormSectionFilledCard';
import { CurrencyUnitSelector } from 'components/BusinessToolForm/components/CurrencyUnitSelector/CurrencyUnitSelector';

import { IRow } from 'helpers/businessTool';
import { concatWithDot } from 'helpers/string';

import { PersonalExpansesTable } from './components';
import { PERSONNEL_EXPENSES_FORM_NAMES, personnelExpensesColumnsNames } from './utils/form';
import { personnelExpensesPrefix } from '../utils/form';

const FIRST_COLUMN_WIDTH = 154;

export const PersonnelExpensesForm: React.FC = () => {
  const { t } = useTranslation();

  const { control, getValues, setValue } = useFormContext();

  const canAccess = useCheckSubSectionAccess(
    FOR_BANK_SECTIONS.FINANCIAL_PLAN,
    FOR_BANK_SUB_SECTIONS.FOR_BANK_FINANCIAL_PLAN.PLANNED_EXPENSES,
  );

  const rowsName = concatWithDot(
    personnelExpensesPrefix,
    PERSONNEL_EXPENSES_FORM_NAMES.PERSONNEL_EXPENSES_VARIANTS,
    ':index',
  );

  const tableName = concatWithDot(personnelExpensesPrefix, PERSONNEL_EXPENSES_FORM_NAMES.PERSONNEL_EXPENSES_TABLE);
  const tableTotalName = concatWithDot(personnelExpensesPrefix, PERSONNEL_EXPENSES_FORM_NAMES.PERSONNEL_EXPENSES_TOTAL);
  const headerName = concatWithDot(personnelExpensesPrefix, PERSONNEL_EXPENSES_FORM_NAMES.PERSONNEL_EXPENSES_HEADER);

  const { fields, update } = useFieldArray({ control, name: tableName });

  const totalExpansesRow = getValues(tableTotalName);

  const totalEmployees = fields.reduce((acc, curr: IRow) => acc + Number(curr['column0']), 0);

  const handleUpdateTotalNumberOfEmployees = (totalEmployees: number) => {
    setValue(tableTotalName, {
      ...totalExpansesRow,
      column0: totalEmployees || '',
      column1:
        totalEmployees && totalExpansesRow.column2
          ? (Number(totalExpansesRow.column2) / Number(totalEmployees)).toFixed(2)
          : '',
    });
  };

  useDebouncedSave({ data: totalEmployees, saveFunction: handleUpdateTotalNumberOfEmployees, debounceTime: 500 });

  return (
    <FormSectionCard
      collapsible
      title={t('financialPlan.plannedExpenses.personnelExpenses.title')}
      disabled={!canAccess}
    >
      <>
        <CurrencyUnitSelector
          disabled={!canAccess}
          containerProps={{ justifyContent: 'space-between', marginBottom: '22px' }}
        />
        <FormSectionFilledCard>
          <PersonalExpansesTable
            showSumRow
            rows={fields}
            name={tableName}
            disabled={!canAccess}
            editableColumn={false}
            columnsNames={personnelExpensesColumnsNames ?? []}
            firstColumnWidth={FIRST_COLUMN_WIDTH}
            watchRowTitleName={rowsName}
            headerName={headerName}
            totalName={tableTotalName}
            updateRow={update}
          />
        </FormSectionFilledCard>
      </>
    </FormSectionCard>
  );
};
